import {
  IonBackButton,
  IonButtons,
  IonHeader,
  IonPage,
  IonToolbar,
} from "@ionic/react"
import {
  Editor,
  Tldraw,
  TLEventMapHandler,
  TLPageId,
  useEditor,
  useValue,
  useEditorComponents,
  createShapeId,
  toRichText,
  TLShapeId,
  TLShape,
  TLGroupShape,
  TLParentId,
  Box,
  TLAssetStore,
  uniqueId,
  StateNode,
  TLDocument,
  RecordId,
  defaultBindingUtils,
  defaultShapeUtils,
} from "tldraw"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { DrawStrokeTool } from "../tools/DrawStrokeTool"
import { useParams } from "react-router"
import { DrawingId } from "../entities"
import { throttle } from "tldraw"
import { EditableDrawingName } from "../components/EditableDrawingName"
import { InnerCanvas } from "../canvas-ui/InnerCanvas"

import { Title } from "../components/Title"
import { BucketTool } from "../tools/BucketTool"
import { CustomNudgeTool } from "../tools/CustomNudgeTool"
import { CustomDrawShapeProps } from "../../shared/CustomDrawShape"
import { LDArrowTool } from "../tools/LDArrowTool"
import { TextTool } from "../tools/LDTextTool"
import { LDSelectTool } from "../tools/SelectTool/LDSelectTool"
import { LDOriginTool } from "../tools/LDOriginTool"
import { LDPage } from "../../shared/LDPage"
import { MarkerTool } from "../tools/MarkerTool"
import { EraserTool } from "../tools/ LDEraserTool"
import { LDContextMenu } from "../canvas-ui/LDContextMenu"
import { Session } from "../Session"
import { Authenticated, useConvex, useMutation, useQuery } from "convex/react"
import { api } from "../convex/_generated/api"
import { LDGeoShapeTool } from "../tools/GeoShape/LDGeoShapeTool"
import { LDLineShapeTool } from "../tools/LineShape/LDLineShapeTool"
import { LDTextShape } from "../../shared/LDTextShape"
import { DrawFillTool } from "../tools/DrawFillTool"
import { LDInstanceMeta } from "../../shared/LDInstance"
import { LDGroupShape } from "../../shared/LDShape"
import { Color } from "../../shared/Expr"
import { useSync } from "@tldraw/sync"
import { initialDocMeta, initialPageMeta } from "../../shared/LDStore"
import { useSession } from "@clerk/clerk-react"
import { Coordinates } from "../components/Coordinates"
import { clearDomCursor, CustomCursor } from "../components/CustomCursor"
import { Origin } from "../components/Origin"
import { FillBindingUtil } from "../shapes/FillBindingUtil"
import { CustomDrawShapeUtil } from "../shapes/CustomDrawShapeUtil"
import { LDLineShapeUtil } from "../shapes/LDLineShapeUtil"
import { LDGroupShapeUtil } from "../shapes/LDGroupShapeUtil"
import { LDGeoShapeUtil } from "../shapes/LDGeoShapeUtil"
import { MarkerShapeUtil } from "../shapes/MarkerShapeUtil"
import { LDTextShapeUtil } from "../shapes/LDTextShapeUtil"

const docId = "document:document" as RecordId<TLDocument>

const multiplayerAssets: TLAssetStore = {
  // to upload an asset, we prefix it with a unique id, POST it to our worker, and return the URL
  async upload(_asset, file) {
    const id = uniqueId()

    const objectName = `${id}-${file.name}`
    const url = `https://sync.livedraw.io/uploads/${encodeURIComponent(objectName)}`

    const response = await fetch(url, {
      method: "PUT",
      body: file,
    })

    if (!response.ok) {
      throw new Error(`Failed to upload asset: ${response.statusText}`)
    }

    return { src: url }
  },
  // to retrieve an asset, we can just use the same URL. you could customize this to add extra
  // auth, or to serve optimized versions / sizes of the asset.
  resolve(asset) {
    return asset.props.src
  },
}

export function DrawingPage() {
  const urlText = new URL(window.location.href).pathname

  const { drawingId } = useParams() as { drawingId: DrawingId }

  const bindingUtils = useMemo(
    () => [...defaultBindingUtils, FillBindingUtil],
    [],
  )
  const shapeUtils = useMemo(
    () => [
      ...defaultShapeUtils,
      CustomDrawShapeUtil,
      LDLineShapeUtil,
      LDGroupShapeUtil,
      LDGeoShapeUtil,
      MarkerShapeUtil,
      LDTextShapeUtil,
    ],
    [],
  )

  const syncServerUrl = import.meta.env.VITE_SYNC_SERVER_URL

  // Create a store connected to multiplayer.
  const store = useSync({
    // We need to know the websocket's URI...
    // uri: `https://sync.livedraw.io/connect/${drawingId}`,
    uri: `${syncServerUrl}/connect/${drawingId}`,
    // ...and how to handle static assets like images & videos
    assets: multiplayerAssets,
    bindingUtils,
    shapeUtils,

    // migrations,
  })

  if (store.status === "synced-remote") {
    // initialize an empty store
    const doc = store.store.get(docId)
    if (doc && Object.keys(doc.meta).length === 0) {
      store.store.update(docId, (doc) => ({
        ...doc,
        meta: initialDocMeta,
      }))
      store.store.update("page:page" as TLPageId, (page) => ({
        ...page,
        meta: initialPageMeta,
      }))
    }
  }

  const currentUser = useSession()

  const drawing = useQuery(
    api.drawings.getDrawing,
    currentUser.isLoaded && currentUser.isSignedIn
      ? {
          drawingId: drawingId,
        }
      : "skip",
  )

  const generateUploadUrl = useMutation(
    api.drawings.generatePreviewImageUploadUrl,
  )

  const updateUserDrawingSettings = useMutation(
    api.drawings.updateUserDrawingSettings,
  ).withOptimisticUpdate((localStore, args) => {
    const drawing = localStore.getQuery(api.drawings.getDrawing, {
      drawingId: args.drawingId,
    })
    if (drawing) {
      localStore.setQuery(
        api.drawings.getDrawing,
        { drawingId: drawing._id },
        {
          ...drawing,
          userSettings: {
            ...drawing.userSettings,
            ...args.settings,
          },
        },
      )
    }
  })

  // sync the drawing/user settings with the editor
  useEffect(() => {
    if (drawing && editorRef.current) {
      editorRef.current.updateInstanceState({
        meta: drawing.userSettings,
        isGridMode: drawing.userSettings.showGrid,
      })
    }
  }, [drawing?.userSettings])

  const updateDrawingName = useMutation(
    api.drawings.updateDrawingName,
  ).withOptimisticUpdate((localStore, args) => {
    const drawing = localStore.getQuery(api.drawings.getDrawing, {
      drawingId: args.drawingId,
    })
    if (drawing) {
      localStore.setQuery(
        api.drawings.getDrawing,
        { drawingId: drawing._id },
        { ...drawing, name: args.name },
      )
    }
  })

  const setDrawingData = useMutation(api.drawings.setDrawingData)

  const editorRef = useRef<Editor | null>(null)
  useEffect(() => {
    if (urlText == "/home") {
      clearDomCursor(editorRef.current!)
    }
  }, [urlText])

  const [editingDrawingName, setEditingDrawingName] = useState<string | null>(
    "",
  )

  // initialize the name input
  useEffect(
    function populate() {
      if (drawing) {
        setEditingDrawingName(drawing.name)
      }
    },
    [drawing?.name],
  )

  const persistDrawingPreview = useCallback(
    throttle(async () => {
      if (!editorRef.current) return

      const shapeIds = editorRef.current!.getCurrentPageShapeIds()
      const bounds = editorRef.current!.getCurrentPageBounds()
      const currentPage = editorRef.current!.getCurrentPage() as LDPage
      if (bounds) {
        if (!currentPage.meta.originSet) {
          editorRef.current.updatePage({
            id: editorRef.current.getCurrentPageId(),
            meta: {
              ...editorRef.current.getCurrentPage().meta,
              origin: {
                x: bounds.center.x,
                y: bounds.center.y,
              },
            },
          })
        }
        editorRef.current!.updatePage({
          id: editorRef.current!.getCurrentPageId(),
          meta: {
            ...editorRef.current!.getCurrentPage().meta,
            originDisplacement: {
              x: currentPage.meta.origin.x - bounds.minX,
              y: currentPage.meta.origin.y - bounds.minY,
            },
          },
        })
      }

      const imageBlob = await editorRef.current!.toImage([...shapeIds], {
        format: "svg",
      })

      const uploadUrl = await generateUploadUrl()

      const response = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": "image/svg+xml" },
        body: imageBlob.blob,
      })

      const { storageId } = await response.json()

      // dispatch(saveDrawingData(drawingId, document, blob))
      setDrawingData({ drawingId, previewImageId: storageId })
    }, 1000),
    [],
  )

  async function handleSaveSnapshot() {
    persistDrawingPreview()
  }

  function logChangeEvent(eventName: string) {
    handleSaveSnapshot()
  }

  function handlePageChanged(pageId: TLPageId, editor: Editor) {
    const currentPage = editor.getPage(pageId) as LDPage
    if (editor.getPages().length === 1) return

    const dependentShapes = editor.getPages().flatMap((p) => {
      const shapeIds = editor.getPageShapeIds(p.id)
      const dependentShapeIds: TLShapeId[] = []
      shapeIds.forEach((shapeId) => {
        const shape = editor.getShape(shapeId)!
        if (
          shape.type === "custom-group" &&
          (shape as LDGroupShape).props.pageId === pageId
        ) {
          dependentShapeIds.push(shapeId)
        }
      })
      return dependentShapeIds
    })
    if (dependentShapes.length === 0) return

    const dependentPageIds = editor
      .getPages()
      .filter((p) => {
        return dependentShapes.some((shapeId) => {
          const shape = editor.getShape(shapeId)
          return (
            shape?.type === "custom-group" &&
            editor.getAncestorPageId(shape) === p.id
          )
        })
      })
      .map((p) => p.id)

    const shapeIds = editor.getPageShapeIds(pageId)

    let commonBounds: Box | undefined
    shapeIds.forEach((shapeId) => {
      const bounds = editor.getShapeMaskedPageBounds(shapeId)
      if (!bounds) return
      if (!commonBounds) {
        commonBounds = bounds.clone()
      } else {
        commonBounds = commonBounds.expand(bounds)
      }
    })

    dependentShapes.forEach((group) => {
      const initialGroupShape = editor.getShape(group) as LDGroupShape
      const groupRotation = initialGroupShape.rotation

      editor.updateShape<LDGroupShape>({
        ...initialGroupShape,
        props: {
          ...initialGroupShape.props,
          parameterValues: {
            ...currentPage.meta.parameterValues,
            ...initialGroupShape.props.parameterValues,
          },
        },
      })

      editor.rotateShapesBy([group], -groupRotation, {
        center: { x: initialGroupShape.x, y: initialGroupShape.y },
      })
      const newShapes = [...shapeIds]
        .map((id) => editor.getShape(id))
        .map((sh) => {
          const shape = sh as TLShape
          const shapeOffsetX = shape!.x - commonBounds!.x
          const shapeOffsetY = shape!.y - commonBounds!.y
          return {
            ...(shape as TLShape),
            id: (shape.id + group) as TLShapeId,
            x:
              shape!.type !== "custom-group"
                ? shapeOffsetX + initialGroupShape.x // - shapeOffsetX
                : shape!.x,
            y:
              shape!.type !== "custom-group"
                ? shapeOffsetY + initialGroupShape.y //- shapeOffsetY
                : shape!.y,
          }
        })
      const shapesToDelete = [
        ...editor.getShapeAndDescendantIds([group]),
      ].filter((id) => {
        return id !== group
      })
      editor.deleteShapes(shapesToDelete)
      editor.createShapes(newShapes)
      const topLevelShapes = newShapes.filter((s) => s.parentId === pageId)

      // editor.rotateShapesBy(
      //   newShapes.map((s) => s.id),
      //   groupRotation,
      //   { center: { x: groupShape.x, y: groupShape.y } },
      // )

      editor.reparentShapes(topLevelShapes, group)

      // other groups
      newShapes
        .filter((s) => s.parentId !== pageId)
        .map((s) => editor.getShape(s.id)!)
        .forEach((shape) => {
          editor.updateShape({
            ...shape,
            parentId: (shape.parentId + group) as TLParentId,
          })
        })

      const newGroupShape = editor.getShape(group) as TLGroupShape

      const newGroupShapeBounds = editor.getShapeMaskedPageBounds(newGroupShape)
      if (!newGroupShapeBounds) return

      editor.rotateShapesBy([group], groupRotation, {
        center: { x: newGroupShape.x, y: newGroupShape.y },
      })
    })

    dependentPageIds.forEach((dependentPageId) => {
      handlePageChanged(dependentPageId, editor)
    })
  }

  const handleChangeEvent2: TLEventMapHandler<"change"> = (change) => {
    // Added
    for (const record of Object.values(change.changes.added)) {
      switch (record.typeName) {
        case "shape":
          if (
            editorRef.current &&
            (record.props as CustomDrawShapeProps)?.isComplete
          ) {
            const pageId = editorRef.current.getAncestorPageId(record)!
            handlePageChanged(pageId, editorRef.current)
          }
          if (record.type === "custom-draw") {
            // only save if the shape is complete
            if ((record.props as CustomDrawShapeProps).isComplete) {
              logChangeEvent(`created shape ${record.type}`)
              return
            }
          }
          if (record.type === "sticker") {
            logChangeEvent(`created shape ${record.type}`)
            return
          }

          break
        case "page":
          break
        default:
      }
    }

    // Updated
    for (const [from, to] of Object.values(change.changes.updated)) {
      switch (to.typeName) {
        case "shape":
          if (
            editorRef.current &&
            !["rotating", "translating", "resizing"].includes(
              editorRef.current?.getCurrentTool().getCurrent()?.id ?? "",
            )
          ) {
            const pageId = editorRef.current.getAncestorPageId(to)!
            handlePageChanged(pageId, editorRef.current)
          }
          if (to.type === "custom-draw") {
            // only save if the shape is complete
            if ((to.props as CustomDrawShapeProps).isComplete) {
              logChangeEvent(`updated shape ${to.type}`)
              return
            }
          }
          break
        // save anytime the page is modified
        case "page":
          logChangeEvent(`updated page ${to}`)
          break
        case "document":
          logChangeEvent(`updated document ${to}`)
          break
      }
    }

    // Removed
    for (const record of Object.values(change.changes.removed)) {
      switch (record.typeName) {
        case "shape":
          if (
            editorRef.current /// &&
            //record.parentId === editorRef.current.getCurrentPageId()
          ) {
            const pageId = editorRef.current.getAncestorPageId(record)!
            handlePageChanged(
              pageId ?? editorRef.current.getCurrentPageId(),
              editorRef.current,
            )
          }
          if (record.type === "custom-draw") {
            logChangeEvent(`deleted shape ${record.type}`)
            return
          }
          break
        case "page":
          logChangeEvent(`deleted page ${record}`)
          break
        default:
      }
    }
  }

  function handleTldrawMount(editor: Editor) {
    // initialize basic settings
    // while we wait for the user preferences to be loaded
    editor.updateInstanceState({
      // This locks the tool so that when you draw lines and shapes it doesn't go to the select tool
      isToolLocked: true,
      meta: LDInstanceMeta.defaultMeta,
    })

    editor.getStateDescendant(
      "select",
      // @ts-ignore
    )!.children!.idle.handleDoubleClickOnCanvas = () => {
      // Create text shape and transition to editing_shape
      if (editor.getIsReadonly()) return

      if (!editor.options.createTextOnCanvasDoubleClick) return

      editor.markHistoryStoppingPoint("creating text shape")

      const id = createShapeId()

      const { x, y } = editor.inputs.currentPagePoint
      const currentLayerId = (editor.getCurrentPage() as LDPage).meta
        .currentLayer

      editor.createShapes<LDTextShape>([
        {
          id,
          type: "text2",
          x,
          y,
          meta: { layer: currentLayerId },
          props: {
            richText: toRichText(""),
            autoSize: true,
          },
        },
      ])

      const shape = editor.getShape(id)
      if (!shape) return

      const util = editor.getShapeUtil(shape)
      if (editor.getIsReadonly()) {
        if (!util.canEditInReadonly(shape)) {
          return
        }
      }

      editor.setEditingShape(id)
      editor.select(id)
      editor.setCurrentTool("select")
      editor.setSelectedShapes([id])
    }
    // get the stored current page id
    const currentPageId = editor.getDocumentSettings().meta
      .currentPageId as TLPageId

    // window['editor'] = editor

    // apply the stored current page id
    editor.setCurrentPage(currentPageId)

    // editor.updateInstanceState({
    //   isGridMode: true,
    // })

    if (!editor.getPage(currentPageId)?.meta?.origin) {
      editor.updatePage({
        id: editor.getCurrentPageId(),
        meta: {
          ...editor.getCurrentPage().meta,
          origin: { x: 0, y: 0 },
        },
      })
    }

    // editor.createShapes([
    //   ({
    //     id: 'shape:0' as TLShapeId,
    //     type: 'custom-draw',
    //     props: {
    //       isComplete: true,
    //       isClosed: false,
    //       isPen: false,
    //       segments: [
    //         {
    //           type: 'free',
    //           points: [{ x: 10, y: 20 }, { x: 50, y: 450 }, { x: 350, y: 350 }]
    //         } as TLDrawShapeSegment,
    //       ],
    //       scale: 1,
    //       layer: '0',
    //     } as CustomDrawShapeProps
    //   }),
    // ])

    editorRef.current = editor

    // editor.store.listen(
    //   (update) => {
    //   },
    //   { scope: 'document', source: 'user' }
    // )

    // only subscribe to changes to the document
    editor.store.listen(handleChangeEvent2, {
      scope: "document",
      source: "user",
    })
    // editor.updateShapes([

    // ])

    // This code is to only update the dependent pages when the user is done with a select action
    // This is to avoid updating the dependent pages when the user is in the middle of an action, because that causes lag

    // TODO: we should also update the page thumb this way
    const translating = editor.getStateDescendant(
      "select.translating",
    ) as StateNode
    const originalTranslatingOnExit = translating.onExit as any
    translating.onExit = function (...args) {
      originalTranslatingOnExit.apply(this, ...args)
      handlePageChanged(editor.getCurrentPageId(), editor)
    }

    const resizing = editor.getStateDescendant("select.resizing") as StateNode
    const originalResizingOnExit = resizing.onExit as any
    resizing.onExit = function (...args) {
      originalResizingOnExit.apply(this, ...args)
      handlePageChanged(editor.getCurrentPageId(), editor)
    }

    const rotating = editor.getStateDescendant("select.rotating") as StateNode
    const originalRotatingOnExit = rotating.onExit as any
    rotating.onExit = function (...args) {
      originalRotatingOnExit.apply(this, ...args)
      handlePageChanged(editor.getCurrentPageId(), editor)
    }
  }

  async function handleChangeDrawingName(newName: string) {
    updateDrawingName({ drawingId, name: newName })
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/"></IonBackButton>
          </IonButtons>

          <Title>
            <EditableDrawingName
              value={editingDrawingName!}
              onChange={handleChangeDrawingName}
            />
          </Title>

          <IonButtons slot="end" style={{ marginRight: "15px" }}>
            {/* <IonButton
              onClick={() => {
                const pageId = editorRef.current?.getCurrentPageId()
                ionRouter.push(`/viewer/${editorDrawing?.id}/page/${pageId}`)
              }}
            >
              View
            </IonButton> */}
            <Session />
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <Authenticated>
        <Tldraw
          store={store}
          onMount={handleTldrawMount}
          bindingUtils={bindingUtils}
          shapeUtils={shapeUtils}
          tools={[
            DrawStrokeTool,
            BucketTool,
            CustomNudgeTool,
            LDArrowTool,
            TextTool,
            LDSelectTool,
            LDOriginTool,
            MarkerTool,
            EraserTool,
            LDGeoShapeTool,
            LDLineShapeTool,
            DrawFillTool,
          ]}
          initialState="draw-stroke"
          // hide TlDraws Ui and replace with custom UI
          components={{
            ContextMenu: LDContextMenu,
            Toolbar: null,
            MainMenu: null,
            PageMenu: null,

            HelperButtons: null,

            InFrontOfTheCanvas: () => {
              return (
                <>
                  <Origin />
                  <Coordinates />
                </>
              )
            },
            OnTheCanvas: () => {
              const editor = useEditor()

              // [1]
              const renderingShapes = useValue(
                "rendering shapes",
                () => {
                  const hoveredLayer = (editor.getCurrentPage() as LDPage).meta
                    .focusedLayerId
                  return editor
                    .getRenderingShapes()
                    .filter(
                      (shape) =>
                        shape.shape.parentId === editor.getCurrentPageId(),
                    )
                    .filter(
                      (shape) =>
                        (shape.shape.props as CustomDrawShapeProps).layer ===
                        hoveredLayer,
                    )
                    .map((s) => s.shape)
                },
                [editor],
              )

              const groupShapes = useValue(
                "group shapes",
                () => {
                  return editor
                    .getCurrentPageShapes()
                    .filter(
                      (s) =>
                        s.parentId === editor.getCurrentPageId() &&
                        s.type === "custom-group",
                    )
                },
                [editor],
              )

              // [2]
              const { ShapeIndicator } = useEditorComponents()
              if (!ShapeIndicator) return null

              return (
                <>
                  <CustomCursor />
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      zIndex: 9999,
                    }}
                  >
                    {renderingShapes.map(({ id }) => (
                      <ShapeIndicator key={id + "_indicator"} shapeId={id} />
                    ))}
                  </div>
                </>
              )
            },
          }}
          forceMobile
        >
          {drawing && (
            <InnerCanvas
              fillColor={drawing.userSettings.fillColor as Color}
              strokeColor={drawing.userSettings.strokeColor as Color}
              markerRadius={drawing.userSettings.markerRadius}
              showGrid={drawing.userSettings.showGrid}
              showLayers={drawing.userSettings.showLayers}
              strokeWidth={drawing.userSettings.strokeWidth}
              onOnlySelectedLayerChange={(check) =>
                updateUserDrawingSettings({
                  drawingId,
                  settings: { onlySelectedLayer: check },
                })
              }
              onFillColorChange={(color) =>
                updateUserDrawingSettings({
                  drawingId,
                  settings: { fillColor: color },
                })
              }
              onStrokeColorChange={(color) =>
                updateUserDrawingSettings({
                  drawingId,
                  settings: { strokeColor: color },
                })
              }
              onMarkerRadiusChange={(radius) =>
                updateUserDrawingSettings({
                  drawingId,
                  settings: { markerRadius: radius },
                })
              }
              onShowGridChange={(showGrid) =>
                updateUserDrawingSettings({
                  drawingId,
                  settings: { showGrid },
                })
              }
              onShowLayersChange={(showLayers) =>
                updateUserDrawingSettings({
                  drawingId,
                  settings: { showLayers },
                })
              }
              onPencilRadiusChange={(radius) =>
                updateUserDrawingSettings({
                  drawingId,
                  settings: { strokeWidth: radius },
                })
              }
            />
          )}
        </Tldraw>
      </Authenticated>
    </IonPage>
  )
}
